import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface SignUpResult {
  success: boolean;
  needsEmailConfirmation: boolean;
}

interface SignUpData {
  email: string;
  password: string;
  fullName: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  organizationName?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (data: SignUpData) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Track the last user id we propagated downstream. Supabase fires
    // TOKEN_REFRESHED and other events that ship a brand-new session object
    // even when the user did not actually change. If we blindly call setUser
    // on every event, every consumer of AuthContext re-renders → cascading
    // refetches across the app.
    let lastUserId: string | null = null;

    // Critical: do NOT mark loading=false from auth events that fire before
    // the initial getSession() resolves. On a hard refresh of a deep route,
    // the very first onAuthStateChange event can arrive with session=null
    // before localStorage rehydration completes. If we set loading=false then,
    // route guards see "auth ready, no user" and incorrectly redirect.
    let initialSessionResolved = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (import.meta.env.DEV) console.log("Auth state changed:", event);
        const newUserId = newSession?.user?.id ?? null;

        setSession(newSession);
        if (newUserId !== lastUserId) {
          lastUserId = newUserId;
          setUser(newSession?.user ?? null);
          // Sentry user identification — fire-and-forget
          import("@/lib/sentry").then(({ setSentryUser, clearSentryUser }) => {
            if (newSession?.user) {
              setSentryUser({ id: newSession.user.id, email: newSession.user.email });
            } else {
              clearSentryUser();
            }
          }).catch(() => {});
        }
        if (initialSessionResolved) {
          setLoading(false);
        }
      }
    );

    // THEN check for existing session — this is the source of truth for
    // route guards on first paint / refresh.
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      const existingUserId = existingSession?.user?.id ?? null;
      setSession(existingSession);
      if (existingUserId !== lastUserId) {
        lastUserId = existingUserId;
        setUser(existingSession?.user ?? null);
        import("@/lib/sentry").then(({ setSentryUser, clearSentryUser }) => {
          if (existingSession?.user) {
            setSentryUser({ id: existingSession.user.id, email: existingSession.user.email });
          } else {
            clearSentryUser();
          }
        }).catch(() => {});
      }
      initialSessionResolved = true;
      setLoading(false);
    }).catch(() => {
      // Even on failure, release the gate so the UI does not hang forever.
      initialSessionResolved = true;
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async ({ email, password, fullName, phoneCountryCode, phoneNumber, organizationName }: SignUpData): Promise<SignUpResult> => {
    try {
      // Use dedicated callback route for email verification
      const redirectUrl = `${window.location.origin}/auth/callback`;

      // Mark user as international if phone country code is NOT +91 (India)
      const isInternational = phoneCountryCode && phoneCountryCode !== '+91';

      // Read tracking params from sessionStorage
      let trackingParams: Record<string, string> = {};
      try {
        const stored = sessionStorage.getItem('tracking_params');
        if (stored) trackingParams = JSON.parse(stored);
      } catch { /* ignore */ }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            phone_country_code: phoneCountryCode,
            phone_number: phoneNumber,
            is_international: isInternational,
            msg91_email_verified: false,
            msg91_mobile_verified: false,
            tracking_params: trackingParams,
            organization_name: organizationName || undefined,
          },
        },
      });

      // Handle timeout errors gracefully - the account is still created and email sent in background
      if (error) {
        const isTimeoutError = error.message?.includes('timeout') ||
          error.message?.includes('5.000000 seconds') ||
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('network') ||
          error.message?.includes('hook_timeout');

        if (!isTimeoutError) {
          throw error;
        }

        // For timeout errors, the account is likely created and email sent
        if (import.meta.env.DEV) console.log("Sign up timed out but likely succeeded");
        return { success: true, needsEmailConfirmation: true };
      }

      if (data.user) {
        // Check if the user already exists by checking if identities array is empty
        // Supabase returns user with empty identities for security (no email enumeration)
        if (data.user.identities && data.user.identities.length === 0) {
          // User already exists - throw error to trigger "already registered" UI
          throw new Error("User already registered");
        }

        // Update profiles table with is_international flag (async, don't block signup)
        if (isInternational) {
          setTimeout(() => {
            supabase
              .from('profiles')
              .update({ is_international: true })
              .eq('id', data.user!.id)
              .then(({ error: profileError }) => {
                if (profileError && import.meta.env.DEV) {
                  console.error('Failed to update profile is_international:', profileError);
                }
              });
          }, 0);
        }

        // Check if email confirmation is required (session will be null if confirmation needed)
        if (data.session) {
          // Email auto-confirmed or confirmation not required - user is logged in
          return { success: true, needsEmailConfirmation: false };
        } else {
          // Email confirmation required - new user
          return { success: true, needsEmailConfirmation: true };
        }
      }

      return { success: false, needsEmailConfirmation: false };
    } catch (error: any) {
      if (import.meta.env.DEV) console.error("Sign up error:", error);

      // Double-check for timeout errors that might have been thrown
      const isTimeoutError = error.message?.includes('timeout') ||
        error.message?.includes('5.000000 seconds') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('network') ||
        error.message?.includes('hook_timeout');

      if (isTimeoutError) {
        // Timeout errors during signup usually mean the account was created successfully
        if (import.meta.env.DEV) console.log("Sign up timed out in catch block, treating as success");
        return { success: true, needsEmailConfirmation: true };
      }

      // Check for "already registered" variants (from Supabase or our own error)
      const isAlreadyRegistered = error.message?.toLowerCase().includes("already registered") ||
        error.message?.toLowerCase().includes("user already registered");

      if (!isAlreadyRegistered) {
        // Only show toast for non-"already registered" errors
        // "already registered" is handled with dedicated UI in Auth page
        toast.error("Sign up failed: " + error.message);
      }
      throw error;
    }
  };

  const signIn = async (email: string, password: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
        toast.success("Welcome back!");
        navigate("/");
        return true;
      }
      return false;
    } catch (error: any) {
      if (import.meta.env.DEV) console.error("Sign in error:", error);
      const errorMsg = error.message?.toLowerCase() || '';

      // Check for email not confirmed error
      if (errorMsg.includes("email not confirmed") || errorMsg.includes("not confirmed")) {
        toast.error("Please verify your email before signing in. Check your inbox for the verification link.");
        // Throw specific error so UI can show resend option
        throw new Error("EMAIL_NOT_CONFIRMED");
      } else if (error.message.includes("Invalid login credentials")) {
        toast.error("Invalid email or password");
      } else {
        toast.error("Sign in failed: " + error.message);
      }
      return false;
    }
  };

  const signOut = async () => {
    // Clear storage FIRST (synchronously) before any async operations
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.error("Error clearing storage:", e);
    }

    // Clear React state immediately to prevent flashes
    setUser(null);
    setSession(null);

    // Suppress the success toast when the partner-host guard (or any other
    // forced flow) is signing the user out — it shows its own message.
    if (!(window as any).__forcedSignOut) {
      toast.success("Signed out successfully");
    }


    // Attempt server-side sign out (fire and forget - don't wait)
    supabase.auth.signOut().catch(() => {
      // Ignore all errors - we're navigating anyway
    });

    // Use React Router navigation instead of hard reload
    navigate("/auth", { replace: true });
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Return safe defaults during initial render to prevent crashes
    // This can happen briefly during hydration or if context isn't ready
    return {
      user: null,
      session: null,
      loading: true,
      signUp: async (_data: SignUpData): Promise<SignUpResult> => { throw new Error("AuthProvider not ready"); },
      signIn: async () => { throw new Error("AuthProvider not ready"); },
      signOut: async () => { throw new Error("AuthProvider not ready"); },
    };
  }
  return context;
};
